// OrderExplainerTrigger.tsx — Hover trigger that opens the Order Explainer drawer

import React, { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { openOrderExplainerDrawer } from "models/orderExplainer/reducer";
import "./orderExplainerTooltip.scss";

const HOVER_DELAY_MS = 500;

interface OrderExplainerTriggerProps {
	orderDetailId: number | string;
	children: React.ReactNode;
	rowData?: Record<string, any>;
}

const OrderExplainerTrigger: React.FC<OrderExplainerTriggerProps> = ({
	orderDetailId,
	children,
	rowData = {},
}) => {
	const dispatch = useDispatch();
	const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearTimer = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	}, []);

	const handleMouseEnter = useCallback(() => {
		clearTimer();
		hoverTimerRef.current = setTimeout(() => {
			hoverTimerRef.current = null;
			dispatch(
				openOrderExplainerDrawer({
					orderDetailId,
					// clone so Redux freezing does not affect the live grid row
					rowData: { ...rowData },
				})
			);
		}, HOVER_DELAY_MS);
	}, [dispatch, orderDetailId, rowData, clearTimer]);

	const handleMouseLeave = useCallback(() => {
		clearTimer();
	}, [clearTimer]);

	// On mousedown/click: cancel pending drawer open so double-click can enter edit mode.
	// Do not stopPropagation so the event reaches AG Grid for cell editing.
	const handleMouseDown = useCallback(() => {
		clearTimer();
	}, [clearTimer]);

	return (
		<div
			className="order-explainer-trigger"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onMouseDown={handleMouseDown}
		>
			{children}
		</div>
	);
};

export default OrderExplainerTrigger;
